import { useTranslation } from 'react-i18next';

import { Autocomplete, Chip, MenuItem, Stack, TextField } from '@mui/material';

import { useAttributes } from '../../hooks';
import { resolveLocalizedText } from '../../lib/localizedText';
import type { AttributeDefinition, AttributeOption } from '../../model/types';

export interface PublicationRuleAttributeCriteriaProps {
  attributeCode: string;
  values: string[];
  onChangeAttributeCode: (code: string) => void;
  onChangeValues: (values: string[]) => void;
  disabled?: boolean;
}

/**
 * ATTRIBUTE_VALUE rule criteria — an attribute picker (reusing the attribute dictionary, the same
 * data source as `AttributeFiltersPopover`) + a value input whose control shape follows the picked
 * attribute's `type` (SELECT/MULTISELECT pick from its defined options; BOOLEAN toggles true/false;
 * everything else is a free-typed chip list — `values` is always a plain string array on the wire).
 */
export function PublicationRuleAttributeCriteria({
  attributeCode,
  values,
  onChangeAttributeCode,
  onChangeValues,
  disabled,
}: PublicationRuleAttributeCriteriaProps) {
  const { t, i18n } = useTranslation('catalog');
  const attributesQuery = useAttributes();
  const dictionary = attributesQuery.data ?? [];
  const definition = dictionary.find((d) => d.code === attributeCode) ?? null;

  const attributeLabel = (def: AttributeDefinition) =>
    resolveLocalizedText(def.translations, 'label', i18n.language, def.code);

  return (
    <Stack spacing={2}>
      <Autocomplete
        loading={attributesQuery.isLoading}
        disabled={disabled}
        options={dictionary}
        value={definition}
        getOptionLabel={(option) => attributeLabel(option)}
        isOptionEqualToValue={(option, val) => option.code === val.code}
        onChange={(_e, next) => {
          onChangeAttributeCode(next?.code ?? '');
          onChangeValues([]);
        }}
        renderInput={(params) => (
          <TextField {...params} label={t('publicationRulesPanel.criteriaForm.attributePickerLabel')} />
        )}
      />

      {definition && (definition.type === 'SELECT' || definition.type === 'MULTISELECT') ? (
        <Autocomplete
          multiple
          disabled={disabled}
          options={(definition.options ?? []).filter((o) => o.active)}
          value={(definition.options ?? []).filter((o) => values.includes(o.code))}
          getOptionLabel={(option: AttributeOption) =>
            resolveLocalizedText(option.translations, 'label', i18n.language, option.code)
          }
          isOptionEqualToValue={(option, val) => option.code === val.code}
          onChange={(_e, next) => onChangeValues(next.map((o) => o.code))}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  size="small"
                  label={resolveLocalizedText(option.translations, 'label', i18n.language, option.code)}
                  {...tagProps}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField {...params} label={t('publicationRulesPanel.criteriaForm.valuesLabel')} />
          )}
        />
      ) : definition?.type === 'BOOLEAN' ? (
        <TextField
          select
          disabled={disabled}
          label={t('publicationRulesPanel.criteriaForm.valuesLabel')}
          value={values[0] ?? ''}
          onChange={(e) => onChangeValues(e.target.value ? [e.target.value] : [])}
          fullWidth
        >
          <MenuItem value="true">{t('productAttributesPanel.booleanYes')}</MenuItem>
          <MenuItem value="false">{t('productAttributesPanel.booleanNo')}</MenuItem>
        </TextField>
      ) : definition ? (
        <Autocomplete
          multiple
          freeSolo
          disabled={disabled}
          options={[]}
          value={values}
          onChange={(_e, next) => onChangeValues(next.map((v) => String(v)))}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return <Chip key={key} size="small" label={option} {...tagProps} />;
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('publicationRulesPanel.criteriaForm.valuesLabel')}
              helperText={t('publicationRulesPanel.criteriaForm.valuesHelper')}
            />
          )}
        />
      ) : null}
    </Stack>
  );
}
